import fs from 'node:fs'
import os from 'node:os'
import path from 'node:path'

import { parseVercelJson, runVercel } from './runner.js'
import type { NeonProvisionResult, VercelRunner } from './types.js'

const PROVISION_TIMEOUT_MS = 180_000
const ENV_PULL_TIMEOUT_MS = 60_000

interface NeonResourceMeta {
  name?: string
  resourceName?: string
}

/**
 * Provision a Neon Postgres database via `vercel integration add neon` and
 * return its DATABASE_URL, WITHOUT touching the project's `.env.local`
 * (BetterStart owns that file). Uses `--no-env-pull`, then pulls into a temp
 * file to extract the connection string.
 *
 * Runs non-interactively (`--format json`). If the marketplace requires terms
 * acceptance or a sandbox claim, it returns a typed failure so the caller can
 * retry interactively via `provisionNeonInteractive`.
 */
export async function provisionNeon(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const addArgs = ['integration', 'add', 'neon', '--no-env-pull', '--format', 'json']
  if (options.plan) addArgs.push('--plan', options.plan)

  const add = await runVercel(runner, addArgs, {
    cwd,
    mode: 'capture',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    const combined = `${add.stdout}\n${add.stderr}`
    if (/terms/i.test(combined)) return { failure: 'terms-required' }
    if (/claim/i.test(combined)) return { failure: 'claim-required' }
    return { failure: add.timedOut ? 'timeout' : 'provision-failed' }
  }

  const meta = parseVercelJson<NeonResourceMeta>(add.stdout)
  const resourceName = meta?.resourceName ?? meta?.name

  const databaseUrl = await pullDatabaseUrl(runner, cwd, options.env)
  if (!databaseUrl) return { resourceName, failure: 'env-pull-empty' }

  return { databaseUrl, resourceName }
}

/**
 * Interactive fallback for the terms/claim cases: runs `integration add neon`
 * with inherited stdio so the user can accept marketplace terms / claim a
 * sandbox resource in-terminal, then pulls the DATABASE_URL. Callers must stop
 * any active spinner first.
 */
export async function provisionNeonInteractive(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const addArgs = ['integration', 'add', 'neon', '--no-env-pull', '--claim']
  if (options.plan) addArgs.push('--plan', options.plan)

  const add = await runVercel(runner, addArgs, {
    cwd,
    mode: 'inherit',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    return { failure: add.timedOut ? 'timeout' : 'provision-failed' }
  }

  const databaseUrl = await pullDatabaseUrl(runner, cwd, options.env)
  if (!databaseUrl) return { failure: 'env-pull-empty' }

  return { databaseUrl }
}

/** Pull the development env into a temp dotenv, extract a Postgres URL, delete it. */
async function pullDatabaseUrl(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<string | undefined> {
  const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), 'betterstart-vercel-'))
  const tmpEnv = path.join(tmpDir, '.env.pull')

  try {
    const pull = await runVercel(
      runner,
      ['env', 'pull', tmpEnv, '--environment', 'development', '--yes'],
      { cwd, mode: 'capture', timeoutMs: ENV_PULL_TIMEOUT_MS, env }
    )
    if (!pull.success) return undefined
    return readDbUrlFromDotenv(tmpEnv)
  } finally {
    try {
      fs.rmSync(tmpDir, { recursive: true, force: true })
    } catch {
      // best effort — never let temp cleanup abort provisioning
    }
  }
}

/**
 * Extract the best Postgres connection string from a pulled dotenv file. The
 * Neon integration writes `DATABASE_URL` plus pooled variants (`POSTGRES_URL`,
 * etc.); prefer `DATABASE_URL`, then `POSTGRES_URL`, then any `*DATABASE_URL`,
 * then any `postgres://` value.
 */
export function readDbUrlFromDotenv(filePath: string): string | undefined {
  if (!fs.existsSync(filePath)) return undefined

  const vars = new Map<string, string>()
  for (const line of fs.readFileSync(filePath, 'utf-8').split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('#') || !trimmed.includes('=')) continue
    const eq = trimmed.indexOf('=')
    const key = trimmed.slice(0, eq).trim()
    const value = trimmed
      .slice(eq + 1)
      .replace(/^['"]|['"]$/g, '')
      .trim()
    if (key) vars.set(key, value)
  }

  const isPg = (v: string | undefined): v is string =>
    !!v && (v.startsWith('postgres://') || v.startsWith('postgresql://'))

  if (isPg(vars.get('DATABASE_URL'))) return vars.get('DATABASE_URL')
  if (isPg(vars.get('POSTGRES_URL'))) return vars.get('POSTGRES_URL')
  for (const [key, value] of vars) {
    if (/DATABASE_URL$/.test(key) && isPg(value)) return value
  }
  for (const value of vars.values()) {
    if (isPg(value)) return value
  }
  return undefined
}
